/**
 * Utilitários para processamento de imagens
 * Inclui funções para manipulação de canvas, máscaras e exportação
 */

/**
 * Carrega uma imagem a partir de um arquivo
 * @param {File} file - Arquivo de imagem
 * @returns {Promise<HTMLImageElement>} - Imagem carregada
 */
export const loadImageFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Carrega uma imagem a partir de uma URL
 * @param {string} url - URL da imagem
 * @returns {Promise<HTMLImageElement>} - Imagem carregada
 */
export const loadImageFromUrl = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Redimensiona uma imagem mantendo a proporção
 * Limita largura máxima a 1280px, respeitando tamanho original se menor
 * @param {HTMLImageElement} img - Imagem original
 * @param {number} maxWidth - Largura máxima (padrão: 1280)
 * @returns {HTMLCanvasElement} - Canvas com imagem redimensionada
 */
export const resizeImage = (img, maxWidth = 1280) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  let { width, height } = img;

  // Apenas redimensiona se a largura for maior que o limite
  if (width > maxWidth) {
    height = Math.round((height * maxWidth) / width);
    width = maxWidth;
  }

  canvas.width = width;
  canvas.height = height;
  ctx.drawImage(img, 0, 0, width, height);

  return canvas;
};

/**
 * Aplica máscara à imagem original criando transparência
 * @param {HTMLCanvasElement} imageCanvas - Canvas com imagem original
 * @param {ImageData} mask - Máscara de segmentação
 * @returns {HTMLCanvasElement} - Canvas com imagem com fundo transparente
 */
export const applyMask = (imageCanvas, mask) => {
  const canvas = document.createElement('canvas');
  canvas.width = imageCanvas.width;
  canvas.height = imageCanvas.height;
  const ctx = canvas.getContext('2d');

  // Desenha imagem original
  ctx.drawImage(imageCanvas, 0, 0);

  // Pega os dados da imagem
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const { data } = imageData;

  // Aplica máscara pixel por pixel
  for (let i = 0; i < data.length; i += 4) {
    const maskValue = mask.data[i]; // Valor da máscara (0-255)
    data[i + 3] = maskValue; // Define o canal alpha
  }

  ctx.putImageData(imageData, 0, 0);
  return canvas;
};

/**
 * Aplica feathering (suavização de bordas) à máscara
 * @param {ImageData} mask - Máscara original
 * @param {number} amount - Quantidade de feathering (0-100)
 * @returns {ImageData} - Máscara com feathering aplicado
 */
export const applyFeathering = (mask, amount) => {
  if (amount === 0) return mask;

  const canvas = document.createElement('canvas');
  canvas.width = mask.width;
  canvas.height = mask.height;
  const ctx = canvas.getContext('2d');

  ctx.putImageData(mask, 0, 0);

  // Aplica blur gaussiano
  const blurAmount = (amount / 100) * 10;
  ctx.filter = `blur(${blurAmount}px)`;
  ctx.drawImage(canvas, 0, 0);

  return ctx.getImageData(0, 0, canvas.width, canvas.height);
};

/**
 * Expande ou contrai a máscara
 * @param {ImageData} mask - Máscara original
 * @param {number} amount - Quantidade (-50 a 50)
 * @returns {ImageData} - Máscara modificada
 */
export const expandMask = (mask, amount) => {
  if (amount === 0) return mask;

  const canvas = document.createElement('canvas');
  canvas.width = mask.width;
  canvas.height = mask.height;
  const ctx = canvas.getContext('2d');

  ctx.putImageData(mask, 0, 0);

  // Usa operações de composição para expandir/contrair
  const iterations = Math.abs(amount);
  const operation = amount > 0 ? 'lighter' : 'darken';

  for (let i = 0; i < iterations; i++) {
    ctx.globalCompositeOperation = operation;
    ctx.drawImage(canvas, -1, 0);
    ctx.drawImage(canvas, 1, 0);
    ctx.drawImage(canvas, 0, -1);
    ctx.drawImage(canvas, 0, 1);
  }

  return ctx.getImageData(0, 0, canvas.width, canvas.height);
};

/**
 * Aplica suavização à máscara usando filtro de mediana
 * @param {ImageData} mask - Máscara original
 * @param {number} amount - Quantidade de suavização (0-100)
 * @returns {ImageData} - Máscara suavizada
 */
export const smoothMask = (mask, amount) => {
  if (amount === 0) return mask;

  const canvas = document.createElement('canvas');
  canvas.width = mask.width;
  canvas.height = mask.height;
  const ctx = canvas.getContext('2d');

  ctx.putImageData(mask, 0, 0);

  // Aplica blur para suavizar
  const blurAmount = (amount / 100) * 5;
  ctx.filter = `blur(${blurAmount}px)`;
  ctx.drawImage(canvas, 0, 0);

  return ctx.getImageData(0, 0, canvas.width, canvas.height);
};

/**
 * Exporta canvas para blob PNG
 * @param {HTMLCanvasElement} canvas - Canvas para exportar
 * @returns {Promise<Blob>} - Blob da imagem PNG
 */
export const canvasToBlob = (canvas) => {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, 'image/png');
  });
};

/**
 * Faz download de um blob como arquivo
 * @param {Blob} blob - Blob para download
 * @param {string} filename - Nome do arquivo
 */
export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Clona ImageData
 * @param {ImageData} imageData - ImageData original
 * @returns {ImageData} - Clone
 */
export const cloneImageData = (imageData) => {
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext('2d');
  ctx.putImageData(imageData, 0, 0);
  return ctx.getImageData(0, 0, canvas.width, canvas.height);
};

/**
 * Cria máscara vazia (toda preta)
 * @param {number} width - Largura
 * @param {number} height - Altura
 * @returns {ImageData} - Máscara vazia
 */
export const createEmptyMask = (width, height) => {
  return new ImageData(width, height);
};

/**
 * Cria máscara cheia (toda branca)
 * @param {number} width - Largura
 * @param {number} height - Altura
 * @returns {ImageData} - Máscara cheia
 */
export const createFullMask = (width, height) => {
  const imageData = new ImageData(width, height);
  const { data } = imageData;

  for (let i = 0; i < data.length; i += 4) {
    data[i] = 255;     // R
    data[i + 1] = 255; // G
    data[i + 2] = 255; // B
    data[i + 3] = 255; // A
  }

  return imageData;
};
